/**
 * Layout model for the customisable organisation dashboard (FG-8053).
 *
 * The dashboard is a 12-column grid of columns. A column holds items, and an
 * item is either a card or a split — two side-by-side stacks whose widths add
 * up to the column. Nesting stops there: a split's parts hold cards only.
 */

/** Column widths are expressed in twelfths, matching MUI's Grid. */
export type ColumnSpan = number;

export interface DashboardCardItem {
  /** Key into the card catalogue. */
  id: string;
}

export interface DashboardSplitPart {
  /** Width in twelfths. Both parts add up to the column's own width. */
  width: ColumnSpan;
  items: DashboardCardItem[];
}

export interface DashboardSplitItem {
  split: true;
  parts: [DashboardSplitPart, DashboardSplitPart];
}

export type DashboardItem = DashboardCardItem | DashboardSplitItem;

export interface DashboardColumn {
  width: ColumnSpan;
  items: DashboardItem[];
}

export interface DashboardLayout {
  columns: DashboardColumn[];
}

/**
 * Where a card sits. For a card directly in a column, `item` is its index. For
 * a card inside a split, `item` is the split's index, `part` picks the side and
 * `card` is the index within that side.
 */
export interface DashboardPath {
  column: number;
  item: number;
  part?: number;
  card?: number;
}

// Takes an optional item so lookups that may miss - an index past the end, an
// `at(-1)` on an empty column - can narrow in one step.
export const isSplitItem = (
  item: DashboardItem | undefined,
): item is DashboardSplitItem =>
  (item as DashboardSplitItem | undefined)?.split === true;
